/**
 * 主图创建详情勾选 id：composite-{formIndex}-{groupIndex}-{resultIndex}
 * 与列表渲染去重顺序一致。
 */

import {
  normalizeTemplateImageRole,
  TEMPLATE_IMAGE_ROLE_MAIN,
} from '@/image-designer/utils/templateImageHierarchy';

export function getDedupedCompositeResultsForGroup(form, groupIndex) {
  const compositeResults = form.compositeResults || [];
  const gi = Number(groupIndex);
  return compositeResults
    .filter((r) => Number(r?.groupIndex ?? 0) === gi)
    .filter((result, idx, arr) => {
      const uniqKey = [
        result?.groupIndex ?? '',
        result?.imageId ?? '',
        result?.replacedTemplateImageId ?? '',
        result?.compositeUrl ?? '',
      ].join('|');
      return arr.findIndex((item) => {
        const itemKey = [
          item?.groupIndex ?? '',
          item?.imageId ?? '',
          item?.replacedTemplateImageId ?? '',
          item?.compositeUrl ?? '',
        ].join('|');
        return itemKey === uniqKey;
      }) === idx;
    });
}

export function buildGoodsCompositeSelectionId(formIndex, groupIndex, resultIndex) {
  return `composite-${formIndex}-${groupIndex}-${resultIndex}`;
}

/** 按模版顺序 → 组顺序 → 组内合成顺序，与详情页展示顺序一致 */
export function buildFlatGoodsCompositeSelectionIds(goodsForms) {
  const ids = [];
  (goodsForms || []).forEach((form, formIndex) => {
    const groupCount = form.imageGroups?.length || 0;
    for (let groupIndex = 0; groupIndex < groupCount; groupIndex++) {
      const groupResults = getDedupedCompositeResultsForGroup(form, groupIndex);
      for (let resultIndex = 0; resultIndex < groupResults.length; resultIndex++) {
        ids.push(buildGoodsCompositeSelectionId(formIndex, groupIndex, resultIndex));
      }
    }
  });
  return ids;
}

function isMainTemplateImage(imageMeta) {
  return normalizeTemplateImageRole(imageMeta?.role) === TEMPLATE_IMAGE_ROLE_MAIN;
}

/** 单组印花内主图成品在 deduped results 中的 resultIndex 列表（旧模板无 role 时视为全部主图） */
export function getMainCompositeResultIndicesForGroup(form, groupIndex) {
  const groupResults = getDedupedCompositeResultsForGroup(form, groupIndex);
  const templateImages = form?.templateData?.images || [];
  const hasRoleMeta = templateImages.some((img) => String(img?.role || '').trim());
  if (!hasRoleMeta) {
    return groupResults.map((_, resultIndex) => resultIndex);
  }
  const mainImageIds = new Set(
    templateImages.filter(isMainTemplateImage).map((img) => img.id),
  );
  return groupResults
    .map((result, resultIndex) => ({ result, resultIndex }))
    .filter(({ result }) => mainImageIds.has(result?.imageId))
    .map(({ resultIndex }) => resultIndex);
}

/** 各印花组内第 N 张主图（1-based position）对应的 composite 选中 id */
export function buildPerGroupMainCompositeSelectionIds(goodsForms, position) {
  const positionIndex = Number(position) - 1;
  if (!Number.isFinite(positionIndex) || positionIndex < 0) return [];
  const ids = [];
  (goodsForms || []).forEach((form, formIndex) => {
    const groupCount = form.imageGroups?.length || 0;
    for (let groupIndex = 0; groupIndex < groupCount; groupIndex++) {
      const mainIndices = getMainCompositeResultIndicesForGroup(form, groupIndex);
      const pick = mainIndices[positionIndex];
      if (pick !== undefined) {
        ids.push(buildGoodsCompositeSelectionId(formIndex, groupIndex, pick));
      }
    }
  });
  return ids;
}

/** 快速选中菜单项数：各组主图数量的最大值 */
export function getMaxMainCompositePositionCount(goodsForms) {
  let max = 0;
  (goodsForms || []).forEach((form) => {
    const groupCount = form.imageGroups?.length || 0;
    for (let groupIndex = 0; groupIndex < groupCount; groupIndex++) {
      const count = getMainCompositeResultIndicesForGroup(form, groupIndex).length;
      if (count > max) max = count;
    }
  });
  return max;
}

const GOODS_COMPOSITE_SELECTION_ID_RE = /^composite-(\d+)-(\d+)-(\d+)$/;

export function parseGoodsCompositeSelectionId(compositeId) {
  const m = compositeId.match(GOODS_COMPOSITE_SELECTION_ID_RE);
  if (!m) return null;
  return {
    formIndex: parseInt(m[1], 10),
    groupIndex: parseInt(m[2], 10),
    resultIndex: parseInt(m[3], 10),
  };
}

/** 旧版两段 id：composite-{groupIndex}-{resultIndex}（多模版时会冲突，仅兼容历史数据） */
const LEGACY_RE = /^composite-(\d+)-(\d+)$/;

export function parseGoodsCompositeSelectionIdLegacy(compositeId) {
  const m = compositeId.match(LEGACY_RE);
  if (!m) return null;
  return {
    groupIndex: parseInt(m[1], 10),
    resultIndex: parseInt(m[2], 10),
  };
}
